import { Component, UIEventHandler, MouseEvent, CSSProperties } from 'react'
import * as debounce from 'lodash/debounce'
import * as styles from 'styles/atoms/Scrollable.css'
import * as cn from 'classnames'
import { QAMarked } from 'interfaces/component'

const MIN_SCROLLED_PX_FOR_FADE = 5
export const MIN_SCROLL_STEP_PX = 16 // used in case of too fast scroll speed of mouse wheel [FRO-1473]

/**
 * Copy-pasted and rewritten https://github.com/rommguy/react-custom-scroll
 */

function ensureWithinLimits (value: number, min: number, max: number) {
  const correctedMin = (!min && min !== 0) ? value : min
  const correctedMax = (!max && max !== 0) ? value : max
  if (correctedMin > correctedMax) {
    return value
  }
  if (value < correctedMin) {
    return correctedMin
  }
  if (value > correctedMax) {
    return correctedMax
  }
  return value
}

function isEventPosOnDomNode (event: MouseEvent<Element>, domNode: Element): boolean {
  const nodeClientRect = domNode.getBoundingClientRect()

  return event.clientX > nodeClientRect.left &&
    event.clientX < nodeClientRect.left + nodeClientRect.width &&
    event.clientY > nodeClientRect.top &&
    event.clientY < nodeClientRect.top + nodeClientRect.height
}

type CalculatedStyle = {
  top: number
  height: number
}

export type ScrollableProps = QAMarked & {
  allowOuterScroll?: boolean
  heightRelativeToParent?: string
  onScroll?: UIEventHandler<HTMLDivElement>
  addScrolledClass?: boolean
  alwaysShowScrollbar?: boolean
  freezePosition?: boolean
  shouldFade?: boolean
  handleClass?: string
  minScrollHandleHeight?: number
  flexBox?: boolean
  flex?: number
  contentClassName?: string
  contentWrapperRef?: (el: HTMLDivElement | null) => void
  innerContainerRef?: (el: HTMLDivElement | null) => void
}

type ScrollableState = {
  scrollPosition: number
  onDrag: boolean
}

const MIN_SCROLL_HANDLE_HEIGHT = 38

class Scrollable extends Component<ScrollableProps, ScrollableState> {
  static defaultProps: Partial<ScrollableProps> = {
    handleClass: styles.innerHandle,
    minScrollHandleHeight: MIN_SCROLL_HANDLE_HEIGHT,
    contentClassName: '',
  }

  state = {
    scrollPosition: 0,
    onDrag: false,
  }

  private scrollHeight: number
  private clientHeight: number
  private scrollbarYWidth: number
  private scrollbarYHeight: number
  private scrollRatio: number
  private hasScroll: boolean
  private scrollHandleHeight: number
  private startDragHandlePos: number
  private startDragMousePos: number

  private scrollable: HTMLDivElement | null = null
  private innerContainer: HTMLDivElement | null = null
  private contentWrapper: HTMLDivElement | null = null
  private customScrollbar: HTMLDivElement | null = null
  private scrollHandle: HTMLDivElement | null = null

  private contentWrapperHeight: number | null = null

  private get scrollBottom () {
    return this.scrollHeight - this.state.scrollPosition - this.clientHeight
  }

  render () {
    const {
      handleClass,
      contentClassName,
      alwaysShowScrollbar,
      children,
      shouldFade,
      qaMarker,
      flexBox,
    } = this.props

    const scrollStyles = this.getScrollStyles()
    const rootClassName = cn(styles.customScroll, {
      [styles.flexBox]: flexBox,
      [styles.scrollHandleDragged]: this.state.onDrag,
    })
    const rootStyle = this.getRootStyles()
    const withNanStyle = this.getScrollHandleStyle()
    const scrollHandleStyle = this.enforceMinHandleHeight(withNanStyle)
    const shouldFadeTop = shouldFade && (this.state.scrollPosition > MIN_SCROLLED_PX_FOR_FADE)
    const shouldFadeBottom = shouldFade && (this.scrollBottom > MIN_SCROLLED_PX_FOR_FADE)

    const scrollbarHandle = this.hasScroll
      ? (
        <div
          ref={ (el) => this.customScrollbar = el }
          className={ cn(styles.customScrollbar, {
            [styles.customScrollbarShownAlways]: alwaysShowScrollbar && this.hasScroll,
          }) }
          key='scrollbar'
        >
          <div
            ref={ (el) => this.scrollHandle = el }
            className={ styles.customScrollHandle }
            style={ scrollHandleStyle }
          >
            <div className={ handleClass } />
          </div>
        </div>
      ) : null

    return (
      <div
        className={ rootClassName }
        ref={ (el) => this.scrollable = el }
        style={ rootStyle }
        data-qa-marker={ qaMarker }
      >
        <div
          className={ cn(styles.outerContainer, {
            [styles.fadeBottom]: shouldFadeBottom,
            [styles.fadeTop]: shouldFadeTop,
          }) }
          style={ this.getOuterContainerStyle() }
          onMouseDown={ this.onMouseDown }
          onClick={ this.onClick }
        >
          { scrollbarHandle }
          <div
            ref={ this.handleInnerContainerRef }
            className={ this.getInnerContainerClasses() }
            style={ scrollStyles.innerContainer }
            onScroll={ this.onScroll }
            data-qa-marker='scrollable'
            data-scroll='allow'
          >
            <div
              className={ contentClassName }
              ref={ this.handleContentWrapperRef }
              style={ scrollStyles.contentWrapper }
            >
              { children }
            </div>
          </div>
        </div>
      </div>
    )
  }

  componentDidMount () {
    this.forceUpdate()
    window.addEventListener('resize', this.handleResizeDebounced)
    if (this.innerContainer) {
      this.innerContainer.addEventListener('wheel', this.blockOuterScroll, { passive: false })
    }
  }

  componentDidUpdate (prevProps: ScrollableProps) {
    const { innerContainer, contentWrapper } = this

    if (!innerContainer || !contentWrapper) {
      return
    }

    this.scrollHeight = innerContainer.scrollHeight
    this.clientHeight = innerContainer.clientHeight
    this.scrollbarYWidth = innerContainer.offsetWidth - innerContainer.clientWidth
    this.scrollRatio = this.scrollHeight ? this.clientHeight / this.scrollHeight : 1

    this.forceUpdateIfNeeded()

    if (this.props.freezePosition || prevProps.freezePosition) {
      this.adjustFreezePosition(prevProps)
    }
  }

  componentWillUnmount () {
    this.handleResizeDebounced.cancel()
    window.removeEventListener('resize', this.handleResizeDebounced)
    document.removeEventListener('mousemove', this.onHandleDrag)
    document.removeEventListener('mouseup', this.onHandleDragEnd)
    if (this.innerContainer) {
      this.innerContainer.removeEventListener('wheel', this.blockOuterScroll)
    }
  }

  public getScrollPosition () {
    return this.state.scrollPosition
  }

  public getScrollHeight () {
    return this.clientHeight
  }

  public updateScrollPosition (scrollPosition: number) {
    const { innerContainer } = this
    if (!innerContainer) {
      return
    }

    innerContainer.scrollTop = scrollPosition
    this.setState({ scrollPosition })
  }

  private forceUpdateIfNeeded () {
    if (!this.innerContainer || !this.contentWrapper) {
      return
    }

    const prevHasScroll = this.hasScroll
    const prevScrollbarYHeight = this.scrollbarYHeight
    this.contentWrapperHeight = this.contentWrapper.clientHeight
    this.hasScroll = this.scrollHeight - this.clientHeight > 1
    this.scrollbarYHeight = Math.max(this.innerContainer.offsetHeight - this.contentWrapperHeight, 0)

    if (this.hasScroll !== prevHasScroll || this.scrollbarYHeight !== prevScrollbarYHeight) {
      this.forceUpdate()
    }
  }

  private adjustFreezePosition (prevProps: ScrollableProps) {
    const { scrollPosition } = this.state
    const { contentWrapper, innerContainer } = this

    if (this.props.freezePosition && contentWrapper) {
      contentWrapper.scrollTop = scrollPosition
    }

    if (prevProps.freezePosition && innerContainer) {
      innerContainer.scrollTop = scrollPosition
    }
  }

  private isMouseEventOnCustomScrollbar (event: MouseEvent<HTMLDivElement>) {
    const { customScrollbar } = this
    if (!customScrollbar) {
      return false
    }

    return isEventPosOnDomNode(event, customScrollbar)
  }

  private isMouseEventOnScrollHandle (event: MouseEvent<HTMLDivElement>) {
    const { scrollHandle } = this
    if (!scrollHandle) {
      return false
    }

    return isEventPosOnDomNode(event, scrollHandle)
  }

  private calculateNewScrollHandleTop (clickEvent: MouseEvent<HTMLDivElement>) {
    if (!this.scrollable) {
      return 0
    }

    const boundingRect = this.scrollable.getBoundingClientRect()
    const currentTop = boundingRect.top + window.pageYOffset
    const clickYRelativeToScrollbar = clickEvent.pageY - currentTop
    const scrollHandleTop = this.getScrollHandleStyle().top
    const isBelowHandle = clickYRelativeToScrollbar > (scrollHandleTop + this.scrollHandleHeight)
    const newScrollHandleTop = isBelowHandle ?
      scrollHandleTop + Math.min(this.scrollHandleHeight, this.clientHeight - this.scrollHandleHeight) :
      scrollHandleTop - Math.max(this.scrollHandleHeight, 0)

    return newScrollHandleTop
  }

  private enforceMinHandleHeight (calculatedStyle: CalculatedStyle): CalculatedStyle {
    const minHeight = this.props.minScrollHandleHeight || MIN_SCROLL_HANDLE_HEIGHT
    if (calculatedStyle.height >= minHeight) {
      return calculatedStyle
    }

    const diffHeightBetweenMinAndCalculated = minHeight - calculatedStyle.height
    const scrollPositionToAvailableScrollRatio = this.state.scrollPosition / (this.scrollHeight - this.clientHeight)
    const scrollHandlePosAdjustmentForMinHeight = diffHeightBetweenMinAndCalculated * scrollPositionToAvailableScrollRatio
    const handlePosition = calculatedStyle.top - scrollHandlePosAdjustmentForMinHeight

    return {
      height: minHeight,
      top: handlePosition,
    }
  }

  private getScrollValueFromHandlePosition (handlePosition: number) {
    return handlePosition / this.scrollRatio
  }

  private getScrollHandleStyle () {
    const handlePosition = this.state.scrollPosition * this.scrollRatio
    this.scrollHandleHeight = this.clientHeight * this.scrollRatio

    return {
      height: this.scrollHandleHeight,
      top: handlePosition,
    }
  }

  private blockOuterScroll = (event: WheelEvent) => {
    if (this.props.allowOuterScroll) {
      return
    }

    const contentNode = event.currentTarget as HTMLDivElement
    const totalHeight = contentNode.scrollHeight
    const { offsetHeight } = contentNode
    const maxScroll = totalHeight - offsetHeight
    const scrollStep = Math.max(
      // keep in viewport the piece of content currently located at the bottom of the scrollable area
      // to let a person to keep the context
      offsetHeight - MIN_SCROLL_STEP_PX,
      MIN_SCROLL_STEP_PX
    )
    const delta = event.deltaY % 3 ? (event.deltaY) : (event.deltaY * 10)

    // scroll top
    if (delta > offsetHeight) {
      contentNode.scrollTop = Math.min(contentNode.scrollTop + scrollStep, maxScroll)
      event.preventDefault()
    } else if (contentNode.scrollTop + delta >= maxScroll) {
      contentNode.scrollTop = maxScroll
      event.preventDefault()
    } else if (delta * -1 > offsetHeight || contentNode.scrollTop + delta <= 0) {
      // scroll bottom
      contentNode.scrollTop = Math.max(contentNode.scrollTop - scrollStep, 0)
      event.preventDefault()
    }

    event.stopPropagation()
  }

  private getInnerContainerClasses () {
    const className = cn(styles.innerContainer, {
      [styles.contentScrolled]: Boolean(this.state.scrollPosition) && Boolean(this.props.addScrolledClass),
    })

    return className
  }

  private getScrollStyles () {
    const scrollSize = this.scrollbarYWidth || 20

    // if content wrapper inside scrollable has zero height (hidden),
    // innerContainer gets the height of scrollbar in Firefox [FRO-721]
    // ```.innerContainer { overflow-y: auto }``` is an easy way to fix the issue
    // but it causes the infinite loop in Firefox [FRO-1192]
    const compensateHeight = this.scrollbarYHeight && this.contentWrapperHeight === 0
      ? -1 * this.scrollbarYHeight
      : 0

    const innerContainerStyle: CSSProperties = {
      marginRight: -1 * scrollSize,
      marginTop: compensateHeight,
      height: (this.props.heightRelativeToParent || this.props.flex) ? '100%' : '',
    }
    const contentWrapperStyle: CSSProperties = {
      marginRight: this.scrollbarYWidth ? 0 : scrollSize,
      height: (this.props.heightRelativeToParent || this.props.flex) ? '100%' : '',
      overflow: this.props.freezePosition ? 'hidden' : 'visible',
    }

    return {
      innerContainer: innerContainerStyle,
      contentWrapper: contentWrapperStyle,
    }
  }

  private getOuterContainerStyle () {
    return {
      height: (this.props.heightRelativeToParent || this.props.flex) ? '100%' : '',
    }
  }

  private getRootStyles () {
    if (this.props.heightRelativeToParent) {
      return {
        height: this.props.heightRelativeToParent,
      }
    }

    if (this.props.flex) {
      return {
        flex: this.props.flex,
      }
    }

    return {}
  }

  private handleInnerContainerRef = (node: HTMLDivElement | null) => {
    const { innerContainerRef } = this.props

    this.innerContainer = node
    if (innerContainerRef) {
      innerContainerRef(node)
    }
  }

  private handleResize = () => {
    this.forceUpdate()
  }

  private handleResizeDebounced = debounce(this.handleResize, 150)

  private handleContentWrapperRef = (el: HTMLDivElement | null) => {
    this.contentWrapper = el

    const { contentWrapperRef } = this.props
    if (contentWrapperRef) {
      contentWrapperRef(el)
    }
  }

  private onClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!this.hasScroll || !this.isMouseEventOnCustomScrollbar(event) || this.isMouseEventOnScrollHandle(event)) {
      return
    }
    const newScrollHandleTop = this.calculateNewScrollHandleTop(event)
    const newScrollValue = this.getScrollValueFromHandlePosition(newScrollHandleTop)

    this.updateScrollPosition(newScrollValue)
  }

  private onScroll: UIEventHandler<HTMLDivElement> = (event) => {
    if (this.props.freezePosition) {
      return
    }

    const scrollPosition = event.currentTarget.scrollTop
    this.setState({ scrollPosition })
    if (this.props.onScroll) {
      this.props.onScroll(event)
    }
  }

  private onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (!this.hasScroll || !this.isMouseEventOnScrollHandle(event)) {
      return
    }

    this.startDragHandlePos = this.getScrollHandleStyle().top
    this.startDragMousePos = event.pageY
    this.setState({
      onDrag: true,
    })
    document.addEventListener('mousemove', this.onHandleDrag)
    document.addEventListener('mouseup', this.onHandleDragEnd)
  }

  private onHandleDrag = (event: DragEvent) => {
    event.preventDefault()
    const mouseDeltaY = event.pageY - this.startDragMousePos
    const handleTopPosition = ensureWithinLimits(this.startDragHandlePos + mouseDeltaY, 0, this.clientHeight - this.scrollHandleHeight)
    const newScrollValue = this.getScrollValueFromHandlePosition(handleTopPosition)
    this.updateScrollPosition(newScrollValue)
  }

  private onHandleDragEnd = (event: DragEvent) => {
    this.setState({
      onDrag: false,
    })
    event.preventDefault()
    document.removeEventListener('mousemove', this.onHandleDrag)
    document.removeEventListener('mouseup', this.onHandleDragEnd)
  }
}

export default Scrollable
